/* eslint-disable react-hooks/exhaustive-deps */
import env from '@beam-australia/react-env'
import moment from 'moment'
import { FC, useContext, useEffect, useState } from 'react'
import { Accept, useDropzone } from 'react-dropzone'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, LabeledText, Paper, Tooltip, Typography, materialIcons } from 'src/components/UI'
import Icon from 'src/components/UI/CustomUI/atoms/Icon'
import MultilineText from 'src/components/UI/CustomUI/atoms/MultilineText'
import Autocomplete from 'src/components/UI/CustomUI/molecules/Autocomplete'
import theme from 'src/components/UI/MaterialUI/theme'
import { filesActions, itemsActions, jobActions, orderActions, ordersActions } from 'src/ducks/actions'
import { employeeIsReadOnly, getEmployee, getEmployeeFullName, getEmployeeId, getJob, getOrderById, getOrder as getOrderDetails } from 'src/ducks/selectors'
import { ORDER_STATUS, PRO_DOCUMENT_TYPE_OPTIONS, ParseFilesUrl, formatDateTimeZone, imageDocExcelTypes, isEmpty, round } from 'src/helpers'
import { WorkOrdersContext } from '../../context'

interface PendingProDoc {
  name: string
  fileUrl: string
  description: string
  uploadedOn: number
  uploadedBy: string
}

const Line: FC<{
  bg: string, color: string, success?: boolean, showButton?: boolean, buttonText?: string,
  upRight?: string | JSX.Element, upLeft?: string | JSX.Element,
  downRight?: string | JSX.Element, downLeft?: string | JSX.Element,
  buttonClick?: () => void, buttonLoading?: boolean, label?: string
  disabled?: boolean
}> = ({
  bg, color, success = false, showButton = false, buttonText = '', label = '',
  upRight, upLeft, downRight, downLeft, buttonClick, buttonLoading = false, disabled = false
}) => {
    return (
      <Grid
        container
        sx={{
          borderTop: '2px solid var(--border-color)',
          padding: '16px',
          backgroundColor: bg,
          display: 'grid',
          gridAutoFlow: 'column',
          gridTemplateColumns: ' min-content min-content auto',
          color: color
        }}>
        <Grid container item spacing={1} width='325px'>
          <Grid item xs={12} sx={{ width: 'max-content' }}>{upLeft}</Grid>
          <Grid item xs={12} sx={{ width: 'max-content' }}>{downLeft}</Grid>
        </Grid>
        <Grid container item spacing={1} sx={{ paddingLeft: 6 }}>
          <Grid item xs={12} sx={{ width: 'max-content' }}>{upRight}</Grid>
          <Grid item xs={12} sx={{ width: 'max-content' }}>{downRight}</Grid>
        </Grid>
        {!isEmpty(label) && (
          <Grid item sx={{
            display: 'flex', width: '100%', paddingRight: '42px',
            justifyContent: 'end', alignItems: 'center'
          }}>
            <Typography variant='h4' color='primary'>{label}</Typography>
          </Grid>)
        }
        {showButton && (
          <Grid item sx={{ display: 'flex', width: '100%', justifyContent: 'end', alignItems: 'center' }}>
            <Button
              disabled={disabled}
              sx={{
                width: '150px', ...(success ? {
                  backgroundColor: theme.palette.green[700],
                  borderColor: theme.palette.green[700],
                  '&:hover': {
                    backgroundColor: theme.palette.green[800],
                    borderColor: theme.palette.green[800],
                  }
                } : {})
              }}
              variant='contained'
              onClick={buttonClick}
            >
              {buttonLoading
                ? <CircularProgress sx={{ color: 'white' }} size={24} />
                : buttonText
              }
            </Button>
          </Grid>
        )}
      </Grid>
    )
  }

const Status: FC = () => {
  const dispatch = useDispatch()
  const [buttonLoading, setButtonLoading] = useState(false)
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [fileToDelete, setFileToDelete] = useState<any | null>(null)
  const [addDocOpen, setAddDocOpen] = useState(false)
  const [docType, setDocType] = useState<any>(null)
  const [pendingDocs, setPendingDocs] = useState<PendingProDoc[]>([])
  const [uploadingDoc, setUploadingDoc] = useState(false)
  const [submittingDoc, setSubmittingDoc] = useState(false)
  const [docNotes, setDocNotes] = useState('')
  const [proDocsExpanded, setProDocsExpanded] = useState(true)

  const {
    dispatch: dispatchContext,
    state: { selectedTab, selectedOrderId }
  } = useContext(WorkOrdersContext)

  const { id } = useParams()
  const order = useSelector(getOrderById(selectedOrderId as string))
  const orderDetails = useSelector(getOrderDetails())

  const userFullName = useSelector(getEmployeeFullName)

  const files = orderDetails?.files ?? []
  const userId = useSelector(getEmployeeId)
  const FILES_URL = env('FILES_URL') ?? ''

  const {
    dispatchedTimestamp, checkedInTimestamp, completedTimestamp, paymentAmount,
    scheduledStartTimestamp, scheduledEndTimestamp, bidPrice, proTotalInvoiceAmount,
    vendorPaymentTimestamp,
    companyContact, companyName, statusType, csmAcceptanceTimestamp,
    completedByName, acceptedByName, vendorPaidByName, paymentType
  } = order

  const isReadOnly = useSelector(employeeIsReadOnly)
  const job = useSelector(getJob())
  const timeZone = job?.properties?.territory?.territoryTimeZone ? job.properties.territory.territoryTimeZone : ""

  const showPaid = statusType && (ORDER_STATUS.PAID === statusType || vendorPaymentTimestamp != null)
  const showAccepted = statusType && (ORDER_STATUS.COMPLETE === statusType || showPaid || csmAcceptanceTimestamp != null)
  const showComplete = statusType && (ORDER_STATUS.REVIEW === statusType || showAccepted || completedTimestamp != null)
  const showCheckedin = statusType && ([ORDER_STATUS.DISPATCHED, ORDER_STATUS.IN_PROGRESS, ORDER_STATUS.PENDING, ORDER_STATUS.PARENT_REVIEW].includes(statusType) || showComplete)

  const showAcceptButton = !showAccepted
  const showMarkCompleteButton = checkedInTimestamp !== null && !showComplete
  const showCheckedinButton = !checkedInTimestamp && statusType !== ORDER_STATUS.PENDING && !showComplete
  const showBidSentLabel = statusType === ORDER_STATUS.PENDING && !showCheckedinButton

  useEffect(() => {
    if (!selectedOrderId) return
    dispatch(orderActions.getOrder(selectedOrderId as string))
  }, [selectedOrderId])

  useEffect(() => {
    if (showComplete && files.length > 0) {
      setProDocsExpanded(true)
    } else {
      setProDocsExpanded(false)
    }
  }, [showComplete, files.length])

  const resetAddDocForm = () => {
    setDocType(null)
    setPendingDocs([])
    setUploadingDoc(false)
    setSubmittingDoc(false)
    setDocNotes('')
  }

  const handleDropDocs = (droppedFiles: File[]) => {
    if (!droppedFiles?.length || uploadingDoc || submittingDoc) return

    const file = droppedFiles[0]
    if (!file) return

    const fileArray = [file]
    setUploadingDoc(true)
    dispatch(filesActions.uploadFiles(fileArray, (succ, fileNameList) => {
      if (succ && fileNameList && fileNameList.length === fileArray.length) {
        const newEntries: PendingProDoc[] = fileArray.map((f, idx) => ({
          name: f.name,
          fileUrl: ParseFilesUrl({ baseUrl: FILES_URL, avatar: fileNameList[idx] ?? '' }),
          description: f.name ?? '',
          uploadedOn: moment().unix(),
          uploadedBy: userId,
        }))
        setPendingDocs(newEntries)
      } else {
        toast.error('One or more files failed to upload. Please try again.')
      }
      setUploadingDoc(false)
    }))
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDropDocs,
    multiple: false,
    maxFiles: 1,
    accept: imageDocExcelTypes as unknown as Accept,
    disabled: uploadingDoc || submittingDoc,
  })

  const removePendingDoc = (index: number) => {
    setPendingDocs((prev) => prev.filter((_, i) => i !== index))
  }
  const handleSubmitDocs = () => {
    if (!selectedOrderId || !docType || pendingDocs.length === 0 || submittingDoc) return

    const trimmedNotes = docNotes.trim()

    const newDocs = pendingDocs.map((pf) => ({
      ...pf,
      fileType: docType?.key,
      description: docType?.value + ' ' + order.orderPublicId,
      uploadedByUserType: 'Vendor',
      uploadedByName: userFullName,
      ...(trimmedNotes ? { notes: trimmedNotes } : {}),
    })) as any[]

    const nextFiles = [...files, ...newDocs] as any[]

    setSubmittingDoc(true)
    dispatch(orderActions.setOrderValue({ attr: 'files', value: nextFiles }))
    dispatch(orderActions.setOrderAttachments(
      { orderId: selectedOrderId as string, files: nextFiles as any },
      (succ) => {
        if (succ) {
          setAddDocOpen(false)
          resetAddDocForm()
          dispatch(orderActions.getOrder(selectedOrderId as string))
          dispatch(jobActions.fetchJob(id as string))
        } else {
          setSubmittingDoc(false)
          dispatch(orderActions.getOrder(selectedOrderId as string))
        }
      }
    ))
  }

  if (!selectedOrderId || !selectedTab) return null

  return (
    <>
      <Dialog
        open={addDocOpen}
        onClose={() => {
          if (uploadingDoc || submittingDoc) return
          setAddDocOpen(false)
          resetAddDocForm()
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle marginLeft={1} marginTop={1}>Add Document</DialogTitle>
        <DialogContent>
          <Grid container direction="column" spacing={2} sx={{ pt: 1 }}>
            <Grid item>
              <Autocomplete
                label="Document Type"
                placeholder="Select Type"
                options={PRO_DOCUMENT_TYPE_OPTIONS}
                value={docType}
                onChange={(v) => setDocType(v)}
              />
            </Grid>
            <Grid item>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Notes <Typography component="span" variant="body2" color="text.disabled">(optional)</Typography>
              </Typography>
              <MultilineText
                value={docNotes}
                onChange={(e) => setDocNotes(e.target.value)}
                placeholder="Add notes for this upload..."
                disabled={uploadingDoc || submittingDoc}
                maxRows={4}
              />
            </Grid>
            <Grid item>
              <Box
                {...getRootProps()}
                sx={{
                  border: '1px dashed #1A2A55',
                  minHeight: '72px',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: isDragActive ? '#E8ECF5' : '#F0F3FA',
                  cursor: uploadingDoc || submittingDoc ? 'default' : 'pointer',
                  opacity: uploadingDoc || submittingDoc ? 0.85 : 1,
                  flexDirection: 'column',
                  gap: 1,
                  py: 2,
                }}
              >
                <input {...getInputProps()} />
                {uploadingDoc ? (
                  <CircularProgress size={32} />
                ) : (
                  <>
                    <Typography align="center">
                      Click or drag a file here — one at a time; a new file replaces the current selection
                    </Typography>
                    <Icon name="Upload" />
                  </>
                )}
              </Box>
            </Grid>
            {pendingDocs.length > 0 && (
              <Grid item container direction="column" spacing={1}>
                {pendingDocs.map((pf, index) => (
                  <Grid item key={`${pf.fileUrl}-${index}`}>
                    <Box
                      sx={{
                        display: 'flex',
                        padding: '12px',
                        borderRadius: '8px',
                        gap: '8px',
                        alignItems: 'center',
                        backgroundColor: '#F9FAFA',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box display="flex" gap={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                        <Icon name="Description" />
                        <Typography variant="body1" noWrap title={pf.name}>
                          {pf.name}
                        </Typography>
                      </Box>
                      <Button
                        onClick={() => removePendingDoc(index)}
                        disabled={uploadingDoc || submittingDoc}
                        sx={{ padding: '0 !important', flexShrink: 0, minWidth: 0 }}
                      >
                        <Icon name="Delete" color="var(--red600)" />
                      </Button>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            disabled={submittingDoc}
            onClick={() => {
              if (uploadingDoc || submittingDoc) return
              setAddDocOpen(false)
              resetAddDocForm()
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              isReadOnly ||
              pendingDocs.length === 0 ||
              uploadingDoc ||
              !docType ||
              submittingDoc
            }
            onClick={handleSubmitDocs}
            endIcon={submittingDoc ? undefined : <materialIcons.Upload fontSize="small" />}
          >
            {submittingDoc ? <CircularProgress size={20} color="inherit" /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false)
          setFileToDelete(null)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle marginLeft={1} marginTop={1}>Delete file</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
            <Typography>
              This action is permanent and cannot be undone.
            </Typography>
            <Typography>
              Are you sure you want to delete <b>{fileToDelete?.description || fileToDelete?.name || 'this file'}</b>?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setDeleteConfirmOpen(false)
              setFileToDelete(null)
            }}
          >
            Cancel
          </Button>
          <Button
            variant="containedError"
            disabled={
              isReadOnly ||
              !fileToDelete?.id ||
              deletingFileId === fileToDelete?.id
            }
            onClick={() => {
              if (!fileToDelete?.id) return
              const remaining = files.filter((f: any) => f.id !== fileToDelete.id)
              setDeletingFileId(fileToDelete.id)
              setDeleteConfirmOpen(false)
              setFileToDelete(null)

              dispatch(orderActions.setOrderValue({ attr: 'files', value: remaining }))
              dispatch(orderActions.setOrderAttachments(
                { orderId: selectedOrderId as string, files: remaining },
                (succ) => {
                  setDeletingFileId(null)
                  if (!succ) dispatch(orderActions.getOrder(selectedOrderId as string))
                }
              ))
            }}
          >
            {deletingFileId === fileToDelete?.id ? <CircularProgress color='info' size={'1.4rem'} /> : 'Yes, Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {showAccepted && (
        <Line
          color='var(--green800)'
          bg='var(--green100)'
          success
          upRight={
            <Typography variant='body1Bold'>
              {`Days to accept: ${moment.unix(csmAcceptanceTimestamp ?? 0).diff(moment.unix(completedTimestamp ?? 0), 'days') + 1}`}
            </Typography>
          }
          upLeft={<Typography variant='body1Bold'>Work Order Accepted</Typography>}
          downRight={`Marked approved by ${acceptedByName ?? '...'}`}
          downLeft={csmAcceptanceTimestamp ? formatDateTimeZone(csmAcceptanceTimestamp, timeZone, "EEE LLL do y hh:mm aa zzz") : ''}
        />
      )}
      {showComplete && (
        <>
          <Line
            disabled={isReadOnly}
            color='var(--lightBlue800)'
            bg='var(--lightBlue100)'
            showButton={showAcceptButton}
            buttonText='Accept'
            buttonLoading={buttonLoading}
            buttonClick={() => {
              setButtonLoading(true)
              dispatch(ordersActions.updateOrderState(
                { orderId: selectedOrderId ?? '', orderState: 'accepted' },
                (success) => {
                  if (success) {
                    dispatch(ordersActions.fetchOrders(
                      { params: { jobId: id, page: 0, size: 9999 } },
                      (_suc) => { setButtonLoading(false) }
                    ))
                  } else setButtonLoading(false)
                }
              ))
            }}
            upRight={
              <Typography variant='body1Bold'>
                {completedTimestamp && `Days to complete: ${moment.unix(completedTimestamp).diff(moment.unix(dispatchedTimestamp ?? 0), 'days') + 1}`}
              </Typography>
            }
            upLeft={<Typography variant='body1Bold'>Work Order Complete</Typography>}
            downRight={`Marked complete by ${completedByName ?? '...'}`}
            downLeft={completedTimestamp ? formatDateTimeZone(completedTimestamp, timeZone, "EEE LLL do y hh:mm aa zzz") : ''}
          />
        </>
      )}
      {showCheckedin && (
        <Line
          disabled={isReadOnly}
          color='var(--navy700)'
          bg='var(--gray100)'
          showButton={showCheckedinButton || showMarkCompleteButton}
          buttonText={showMarkCompleteButton ? 'Mark Complete' : 'Checkin Vendor'}
          buttonLoading={buttonLoading}
          label={showBidSentLabel ? 'Bid Sent' : ''}
          buttonClick={() => {
            if (isReadOnly) return
            setButtonLoading(true)
            dispatch(ordersActions.updateOrderState(
              {
                orderId: selectedOrderId ?? '',
                orderState: showMarkCompleteButton ? 'complete' : 'checkin'
              },
              (success) => {
                if (success) {
                  dispatch(ordersActions.fetchOrders(
                    { params: { jobId: id, page: 0, size: 9999 } },
                    (_suc) => {
                      dispatch(itemsActions.fetchItemsByCategory({
                        params: { jobId: id, orderId: selectedOrderId || '' }
                      }, (_succ) => {
                        setButtonLoading(false)
                      }))
                    }
                  ))
                } else setButtonLoading(false)
              }
            ))
          }}
          upRight={checkedInTimestamp && checkedInTimestamp > 0
            ? <LabeledText labelValue='Checked In:' textValue={formatDateTimeZone(checkedInTimestamp, timeZone, "EEE LLL do y hh:mm aa zzz")} />
            : <Typography variant='body1Bold' fontStyle='italic'>Not Checked In</Typography>}
          upLeft={`${companyContact} (${companyName})`}
          downRight={<LabeledText labelValue='Bid Price:' textValue={`$${round(bidPrice, 2)}`} />}
          downLeft={`
            ${scheduledStartTimestamp ? formatDateTimeZone(scheduledStartTimestamp, timeZone, "EEE LLL do y hh:mm aa zzz") : ''}
            - ${scheduledEndTimestamp ? formatDateTimeZone(scheduledEndTimestamp, timeZone, "EEE LLL do y hh:mm aa zzz") : ''}    
          `}

        />
      )}
      <Grid container sx={{ px: 2, pb: 2, mt: 1 }} spacing={1}>
        <Grid item xs={12} sx={{ width: 'fit-content', maxWidth: '100%' }}>
          <LabeledText
            labelValue="Amount Customer Invoiced"
            textValue={
              typeof proTotalInvoiceAmount === 'number' && !Number.isNaN(proTotalInvoiceAmount)
                ? `$${round(proTotalInvoiceAmount, 2)}`
                : '—'
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              borderColor: 'rgba(148, 163, 184, 0.45)',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)',
              width: 'fit-content',
              maxWidth: '100%',
            }}
          >
            <Box
              role="button"
              tabIndex={0}
              onClick={() => setProDocsExpanded((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setProDocsExpanded((v) => !v)
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                px: 2,
                py: 1.25,
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { backgroundColor: 'rgba(241, 245, 249, 0.6)' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <materialIcons.ExpandMore
                  fontSize="small"
                  sx={{
                    transform: proDocsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                    color: 'var(--navy700)',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="h6" fontWeight={700} noWrap>
                  Pro Docs
                </Typography>
                <Chip
                  size="small"
                  label={`${files.length}`}
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    backgroundColor: 'rgba(59, 130, 246, 0.12)',
                    color: 'var(--lightBlue800)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                  }}
                />
              </Box>
              <Button
                variant="containedLight"
                disabled={isReadOnly}
                onClick={(e) => {
                  e.stopPropagation()
                  setAddDocOpen(true)
                }}
                endIcon={<materialIcons.Add fontSize="small" />}
                sx={{ height: 32, flexShrink: 0 }}
              >
                Add Document
              </Button>
            </Box>
            <Collapse in={proDocsExpanded}>
              <Divider sx={{ opacity: 0.6 }} />
              {files.length === 0 ? (
                <Box sx={{ px: 2, py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No documents yet.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ px: 0, pb: 0 }}>
                  {files.map((file: any, idx: number) => {
                    const isDeleting = deletingFileId === file.id
                    return (
                      <Grid
                        key={file.id ?? `${file.name}-${idx}`}
                        container
                        alignItems="center"
                        wrap="nowrap"
                        sx={{
                          px: 2,
                          py: 1.25,
                          '&:hover': { backgroundColor: 'rgba(248, 250, 252, 0.9)' },
                        }}
                        spacing={1}
                      >
                        <Grid item xs sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body1Bold"
                            sx={{
                              lineHeight: 1.2,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {file.description || 'Unnamed file'}
                          </Typography>
                        </Grid>
                        <Grid item xs="auto" sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Chip
                            size="small"
                            label={String(file.fileType?.replaceAll('_', ' ') ?? 'UNKNOWN')}
                            sx={{
                              minWidth: 92,
                              fontWeight: 700,
                              backgroundColor: 'rgba(59, 130, 246, 0.10)',
                              color: 'var(--lightBlue800)',
                              border: '1px solid rgba(59, 130, 246, 0.18)',
                              '& .MuiChip-label': {
                                whiteSpace: 'nowrap',
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs="auto" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          {!!String(file.notes ?? '').trim() && (
                            <Tooltip
                              title={String(file.notes ?? '')}
                              slotProps={{
                                tooltip: {
                                  sx: { fontSize: '16px', maxWidth: 420 },
                                },
                              }}
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled
                                  sx={{
                                    border: '1px solid rgba(148, 163, 184, 0.45)',
                                    backgroundColor: 'white',
                                  }}
                                >
                                  <materialIcons.DescriptionOutlined fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="See file">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (!file.fileUrl) return
                                  window.open(file.fileUrl, '_blank', 'noopener,noreferrer')
                                }}
                                disabled={!file.fileUrl}
                                sx={{
                                  border: '1px solid rgba(148, 163, 184, 0.45)',
                                  backgroundColor: 'white',
                                }}
                              >
                                <materialIcons.VisibilityOutlined fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title={isReadOnly ? 'Read only' : 'Delete file'}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={isReadOnly || isDeleting}
                                onClick={() => {
                                  if (isReadOnly) return
                                  setFileToDelete(file)
                                  setDeleteConfirmOpen(true)
                                }}
                                sx={{
                                  border: '1px solid rgba(239, 68, 68, 0.25)',
                                  backgroundColor: 'rgba(239, 68, 68, 0.06)',
                                  color: 'var(--error-color)',
                                }}
                              >
                                {isDeleting
                                  ? <CircularProgress size={16} sx={{ color: 'var(--error-color)' }} />
                                  : <materialIcons.DeleteOutline fontSize="small" />
                                }
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    )
                  })}
                </Box>
              )}
            </Collapse>
          </Paper>
        </Grid>
      </Grid>
    </>
  )
}

export default Status